// Angular
import { Injectable } from '@angular/core';

// Third party
import { BehaviorSubject, Observable } from 'rxjs';

/** Snapshot of the wager the user is currently building. */
export interface IWagerSelectionSnapshot {
  eventId: string;
  runId?: string;
  raceId: number | string;
  poolId: string;
  baseAmount: number | string;
  modifier: string | number;
  /** Per-leg selections, each a comma-separated runner-id string. */
  legs: { Runners: string }[];
}

/**
 * Holds the in-progress wager selection so surfaces outside the
 * runner grid (e.g. the Help panel's "encoded runners" link) can
 * read what the user has selected without touching the DOM.
 */
@Injectable({ providedIn: 'root' })
export class WagerSelectionService {
  private readonly _selection$ = new BehaviorSubject<IWagerSelectionSnapshot | null>(null);
  readonly selection$: Observable<IWagerSelectionSnapshot | null> = this._selection$.asObservable();

  /**
   * Store the current selection (or null to clear it)
   * @param {IWagerSelectionSnapshot | null} snapshot Current selection
   * @returns {void}
   */
  setSelection(snapshot: IWagerSelectionSnapshot | null): void {
    this._selection$.next(snapshot);
  }

  /**
   * Current selection snapshot
   * @returns {IWagerSelectionSnapshot | null} Latest selection, or null
   */
  getSelection(): IWagerSelectionSnapshot | null {
    return this._selection$.getValue();
  }

  /**
   * Clear the stored selection
   * @returns {void}
   */
  clearSelection(): void {
    this._selection$.next(null);
  }

  /**
   * Whether a selection with at least one runner exists
   * @returns {boolean} True when runners are selected
   */
  hasSelection(): boolean {
    return !!this._selection$.getValue()?.legs
      ?.some((leg) => (leg?.Runners ?? '').trim().length > 0);
  }
}
